import {graphNode} from "@cm/graph-v3"
import {isInstanceOf} from "@cm/utils"
import {ChangeDetectionStrategy, Component, computed, signal} from "@angular/core"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, SelectionPossibilities} from "@template-editor/components/value-slot/value-slot.component"
import {ToggleComponent} from "@app/common/components/buttons/toggle/toggle.component"
import type {NumberLikeNode} from "@cm/template-nodes"
import type {
    MattressComponentGraphGenerator,
    MattressComponentGraphGeneratorParameters,
    MattressCurveLikeNode,
    MattressReferenceFrameLikeNode,
    MattressInterfaceDescriptor,
} from "@cm/mattress-nodes"
import {NumberExport, isNumberLikeNode, NumberInfo} from "@cm/template-nodes"
import {
    MattressComponentGraph,
    mattressComponentInstanceParametersParameterValue,
    mattressExport,
    isMattressCurveLikeNode,
    isMattressReferenceFrameLikeNode,
    MattressCurveExport,
    MattressReferenceFrameExport,
    isMattressComponentGraphGeneratorInfoData,
    collectMattressNodes,
    MattressCurveInfo,
    MattressReferenceFrameInfo,
    schemaForDescriptor,
} from "@cm/mattress-nodes"
import {
    MattressComponentInstanceInterfaceInspectorComponent,
    type MattressInputParameterProvider,
} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-component-instance-interface-inspector/mattress-component-instance-interface-inspector.component"

import {z} from "zod/v4-mini"

@Component({
    selector: "cm-mattress-component-graph-generator-inspector",
    templateUrl: "./mattress-component-graph-generator-inspector.component.html",
    styleUrl: "./mattress-component-graph-generator-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent, MattressComponentInstanceInterfaceInspectorComponent, ToggleComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MattressComponentGraphGeneratorInspectorComponent extends BaseInspectorComponent<MattressComponentGraphGenerator> {
    readonly typeSelectionPossibilities: SelectionPossibilities<MattressComponentGraphGeneratorParameters["parameters"]["type"]> = [
        {name: "Unit Repeat 1D", value: "MattressComponentGraphGeneratorUnitRepeat1D"},
    ]

    readonly infoData = computed(() => {
        const generator = this.$node()
        const mattress = generator.getParentMattressNode()
        const infoData = mattress.getMattressNodeInfoData(generator, this.sceneManagerService)
        if (!infoData) return null
        if (isMattressComponentGraphGeneratorInfoData(infoData)) return infoData
        return null
    })

    protected readonly $componentDescriptors = computed(() => {
        const infoData = this.infoData()
        if (!infoData) return []
        return infoData.componentDescriptorList
    })

    protected readonly $templateDescriptors = computed(() => {
        const infoData = this.infoData()
        if (!infoData) return []
        return infoData.templateDescriptorList
    })

    readonly $inputParameterProvider = computed<MattressInputParameterProvider>(() => {
        const parameters = this.$parameters()
        const unitGraph = parameters?.parameters.unitGraph
        const baseGraph = parameters?.parameters.baseGraph

        const unitCurveExports: MattressCurveExport[] =
            unitGraph && unitGraph instanceof MattressComponentGraph ? [...collectMattressNodes(unitGraph, isInstanceOf(MattressCurveExport))] : []
        const unitReferenceFrameExports: MattressReferenceFrameExport[] =
            unitGraph && unitGraph instanceof MattressComponentGraph ? [...collectMattressNodes(unitGraph, isInstanceOf(MattressReferenceFrameExport))] : []
        const unitNumberExports: NumberExport[] =
            unitGraph && unitGraph instanceof MattressComponentGraph ? [...collectMattressNodes(unitGraph, isInstanceOf(NumberExport))] : []
        const contextCurves: MattressCurveLikeNode[] =
            baseGraph && baseGraph instanceof MattressComponentGraph ? [...collectMattressNodes(baseGraph, isMattressCurveLikeNode)] : []
        const contextReferenceFrames: MattressReferenceFrameLikeNode[] =
            baseGraph && baseGraph instanceof MattressComponentGraph ? [...collectMattressNodes(baseGraph, isMattressReferenceFrameLikeNode)] : []
        const contextNumbers = (baseGraph && baseGraph instanceof MattressComponentGraph ? [...collectMattressNodes(baseGraph, isNumberLikeNode)] : []).filter(
            (n): n is NumberLikeNode & {parameters: {name: string}} => "name" in n.parameters,
        )

        const curvePossibilities = [
            {name: "(none)", value: null},
            ...unitCurveExports.map((node) => ({name: `[Prior Unit] ${node.parameters.name}`, value: node})),
            ...contextCurves.map((node) => ({name: node.parameters.name, value: node})),
        ]

        const referenceFramePossibilities = [
            {name: "(none)", value: null},
            ...unitReferenceFrameExports.map((node) => ({name: `[Prior Unit] ${node.parameters.name}`, value: node})),
            ...contextReferenceFrames.map((node) => ({name: node.parameters.name, value: node})),
        ]

        const numberPossibilities = [
            {name: "(none)", value: null},
            ...unitNumberExports.map((node) => ({name: `[Prior Unit] ${node.parameters.name}`, value: node})),
            ...contextNumbers.map((node) => ({name: node.parameters.name, value: node})),
        ]

        return ((key: string, descriptor: MattressInterfaceDescriptor) => ({
            selectionPossibilities:
                descriptor instanceof MattressCurveInfo
                    ? curvePossibilities
                    : descriptor instanceof MattressReferenceFrameInfo
                      ? referenceFramePossibilities
                      : numberPossibilities,
            overwrittenValue: parameters?.parameters.unitInputs[key] ?? null,
            schema: schemaForDescriptor(descriptor, true),
        })) as MattressInputParameterProvider
    })

    changeType($event: unknown) {
        if (typeof $event !== "string") return
        const type = $event as MattressComponentGraphGeneratorParameters["parameters"]["type"]

        let newParameters: MattressComponentGraphGeneratorParameters["parameters"]
        if (type === "MattressComponentGraphGeneratorUnitRepeat1D") {
            newParameters = {
                type: "MattressComponentGraphGeneratorUnitRepeat1D",
                baseGraph: null,
                unitGraph: null,
                unitCount: null,
                unitIdPrefix: null,
                unitInputs: {},
            }
        } else {
            return
        }

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({parameters: newParameters})
        })
    }

    updateUnitInputParameter(key: string, value: unknown) {
        const result = z.union([mattressComponentInstanceParametersParameterValue, mattressExport, graphNode(NumberExport)]).safeParse(value)
        if (!result.success) return
        const generator = this.$node()
        if (generator.parameters.parameters.type !== "MattressComponentGraphGeneratorUnitRepeat1D") return
        this.sceneManagerService.modifyTemplateGraph(() => {
            generator.updateParameters({
                parameters: {
                    ...generator.parameters.parameters,
                    unitInputs: {...generator.parameters.parameters.unitInputs, [key]: result.data},
                },
            })
        })
        this.onUpdatedParameters()
    }

    readonly $showOutputsIfUnitCountZero = signal(false)

    readonly $outputDescriptorsEntries = computed(() => {
        const outputsIfUnitCountZeroParameters = this.$parameters()?.parameters.outputsIfUnitCountZero
        const templateOutputs = this.$templateDescriptors().filter((d): d is NumberInfo => d instanceof NumberInfo && d.props.type === "output")
        const componentOutputs = this.$componentDescriptors().filter(
            (d): d is MattressCurveInfo | MattressReferenceFrameInfo =>
                d instanceof MattressCurveInfo || (d instanceof MattressReferenceFrameInfo && d.props.type === "output"),
        )

        const outputDescriptors = [...templateOutputs, ...componentOutputs]
        return outputDescriptors.map((descriptor) => ({
            descriptor,
            schema: schemaForDescriptor(descriptor, false),
            value: outputsIfUnitCountZeroParameters?.[descriptor.props.id] ?? null,
        }))
    })

    updateOutputIfUnitCountZero(
        descriptorEntry: {descriptor: MattressCurveInfo | MattressReferenceFrameInfo | NumberInfo; schema: z.ZodMiniType<unknown, unknown>},
        value: unknown,
    ) {
        const generator = this.$node()
        if (generator.parameters.parameters.type !== "MattressComponentGraphGeneratorUnitRepeat1D") return

        const descriptor = descriptorEntry.descriptor

        const parsedValue = descriptorEntry.schema.safeParse(value)
        if (!parsedValue.success) return
        const parsed = parsedValue.data as NonNullable<MattressComponentGraphGeneratorParameters["parameters"]["outputsIfUnitCountZero"]>[string] | null

        this.sceneManagerService.modifyTemplateGraph(() => {
            const current = generator.parameters.parameters.outputsIfUnitCountZero ?? {}
            const updated = {...current}
            if (parsed === null) delete updated[descriptor.props.id]
            else updated[descriptor.props.id] = parsed

            generator.updateParameters({
                parameters: {...generator.parameters.parameters, outputsIfUnitCountZero: updated},
            })
        })
        this.onUpdatedParameters()
    }
}
